import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useJourneyAuthoringAccess } from "@/hooks/useJourneyAuthoringAccess";
import type { JourneyWithDays } from "@/hooks/useJourneys";
import { supabase } from "@/integrations/supabase/client";
import { LoadingState } from "@/components/feedback/LoadingState";
import { isCourseJourney } from "@/utils/courseJourneys";
import { journeyCompletionArtwork } from "@/utils/journeyCompletionArtwork";
import {
  getCourseReturnPath,
  getCourseWritingContext,
  type CoursePreviewNavigation,
} from "@/utils/courseJourneyNavigation";
import {
  readAuthorSnapshot,
  type CourseAuthorSnapshot,
} from "@/utils/authorPreview";
import { WritingPreviewProvider } from "./WritingPreviewContext";
import {
  coursePreviewStorageKey,
  readCoursePreviewProgress,
  writeCoursePreviewProgress,
} from "@/utils/courseJourneyPreview";

export interface CoursePreviewContextValue {
  journeyId: string;
  unitId: string | null;
  completeWriting: () => void;
  completedUnitIds: ReadonlySet<string>;
  completeUnit: (unitId: string) => void;
  reset: () => void;
  navigation: CoursePreviewNavigation;
  authorReturnPath: string;
  isUnsaved?: boolean;
}

const CoursePreviewContext = createContext<CoursePreviewContextValue | null>(
  null,
);
export const useCoursePreview = () => useContext(CoursePreviewContext);

interface BoundaryProps {
  children: ReactNode | ((journey: JourneyWithDays) => ReactNode);
  surface: "journey" | "powerprompt" | "freestyle";
  journey?: JourneyWithDays;
  programId?: string;
}

/** A URL requests preview; only authenticated ownership/admin evidence authorizes it. */
export function CoursePreviewBoundary(props: BoundaryProps) {
  const [params] = useSearchParams();
  const requested =
    params.has("preview") ||
    params.has("previewSnapshot") || params.has("previewProgram") ||
    (props.surface === "journey" && props.journey?.is_published === false);
  if (!requested)
    return (
      <>
        {typeof props.children === "function"
          ? props.journey
            ? props.children(props.journey)
            : null
          : props.children}
      </>
    );
  return <AuthorizedCoursePreview {...props} />;
}

function AuthorizedCoursePreview({
  children,
  journey: suppliedJourney,
  surface,
  programId,
}: BoundaryProps) {
  const { user, loading: authLoading } = useAuth();
  const access = useJourneyAuthoringAccess();
  const route = useParams();
  const [params] = useSearchParams();
  const writing = getCourseWritingContext(params);
  const token = params.get("previewSnapshot");
  const stored = useMemo(
    () => readAuthorSnapshot(user?.id, token),
    [user?.id, token],
  );
  const snapshot = stored?.kind === "course" ? stored : null;
  const explicitFlagValid = !params.has("previewProgram") && (token
    ? params.get("preview") === "1"
    : !params.has("preview") || params.get("preview") === "1");
  const validWritingRequest = surface === "journey" || Boolean(writing);
  const journeyId = token
    ? snapshot?.sourceId
    : (suppliedJourney?.id ?? writing?.journeyId);
  const query = useQuery({
    queryKey: ["course-preview-access", user?.id, journeyId],
    enabled: Boolean(
      user?.id &&
      journeyId &&
      (!suppliedJourney || token) &&
      explicitFlagValid &&
      validWritingRequest,
    ),
    retry: false,
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("journeys")
        .select("*, days:journey_days(*)")
        .eq("id", journeyId!)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as JourneyWithDays | null;
    },
  });
  const source = token ? query.data : (suppliedJourney ?? query.data);
  const journey = token ? snapshot?.journey : source;
  const sourceAuthorized =
    snapshot && !snapshot.sourceId
      ? access.canAuthor
      : Boolean(
          source &&
          (source.created_by === user?.id || access.data?.isAdmin === true),
        );
  const boundUnit =
    surface === "journey"
      ? null
      : journey?.days.find((unit) => unit.id === writing?.unitId);
  const authorized = Boolean(
    user?.id &&
    journey &&
    isCourseJourney(journey) &&
    sourceAuthorized &&
    (!token ||
      (snapshot &&
        snapshot.journey.id ===
          (surface === "journey" ? route.journeyId : writing?.journeyId))),
  );
  const unitMatches =
    surface === "journey" ||
    Boolean(
      boundUnit &&
      boundUnit.day_number === writing?.day &&
      boundUnit.unit_type === surface &&
      (surface !== "powerprompt" || boundUnit.program_id === programId),
    );
  const programAccess = useQuery({
    queryKey: ["snapshot-program-access", user?.id, snapshot?.token, programId],
    enabled: Boolean(
      snapshot &&
      authorized &&
      unitMatches &&
      surface === "powerprompt" &&
      programId,
    ),
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("programs")
        .select("id, created_by, program_type")
        .eq("id", programId!)
        .maybeSingle();
      if (error) throw error;
      return Boolean(
        data &&
        ["powerprompt", "powerPrompt"].includes(data.program_type || "") &&
        (data.created_by === user?.id || access.data?.isAdmin === true),
      );
    },
  });

  if (
    authLoading ||
    access.isLoading ||
    ((!suppliedJourney || token) &&
      query.isLoading &&
      query.fetchStatus !== "idle") ||
    (programAccess.isLoading && programAccess.fetchStatus !== "idle")
  ) {
    return <LoadingState variant="page" />;
  }
  if (query.isError || access.isError || programAccess.isError) {
    return (
      <div role="alert" className="mx-auto max-w-xl p-8">
        Your preview couldn't load.{" "}
        <button
          className="underline"
          onClick={() => {
            void query.refetch();
            void access.refetch();
            void programAccess.refetch();
          }}
        >
          Try again
        </button>
      </div>
    );
  }
  if (
    !explicitFlagValid ||
    !validWritingRequest ||
    !authorized ||
    !unitMatches ||
    !journey ||
    !user ||
    (snapshot && surface === "powerprompt" && programAccess.data !== true)
  ) {
    return (
      <div role="alert" className="mx-auto max-w-xl p-8">
        This preview isn't available for your account.{" "}
        <Link className="underline" to="/my-programs">
          Back to my programs
        </Link>
      </div>
    );
  }
  const from =
    (snapshot?.from || params.get("previewFrom")) === "admin" &&
    access.data?.isAdmin === true
      ? "admin"
      : "creator";
  return (
    <AuthorizedPreviewState
      key={`${user.id}:${journey.id}:${journey.updated_at}`}
      userId={user.id}
      journey={journey}
      unitId={boundUnit?.id ?? null}
      from={from}
      snapshot={snapshot}
    >
      {children}
    </AuthorizedPreviewState>
  );
}

function AuthorizedPreviewState({
  userId,
  journey,
  unitId,
  from,
  children,
  snapshot,
}: {
  userId: string;
  journey: JourneyWithDays;
  unitId: string | null;
  from: "admin" | "creator";
  children: BoundaryProps["children"];
  snapshot: CourseAuthorSnapshot | null;
}) {
  const [params] = useSearchParams();
  const allowed = useMemo(
    () => new Set(journey.days.map((unit) => unit.id)),
    [journey.days],
  );
  const key = coursePreviewStorageKey(userId, journey.id, journey.updated_at);
  const [completed, setCompleted] = useState(() =>
    readCoursePreviewProgress(key, allowed),
  );
  const completeUnit = useCallback(
    (id: string) => {
      if (!allowed.has(id)) return;
      // Persist synchronously before a player navigates away on completion.
      const next = readCoursePreviewProgress(key, allowed).add(id);
      writeCoursePreviewProgress(key, next);
      setCompleted(next);
    },
    [allowed, key],
  );
  const reset = useCallback(() => {
    writeCoursePreviewProgress(key, new Set());
    setCompleted(new Set());
  }, [key]);
  const completeWriting = useCallback(() => {
    if (unitId) completeUnit(unitId);
  }, [completeUnit, unitId]);
  const authorParams = new URLSearchParams();
  const currentDay = params.get("day") ?? params.get("journeyDay");
  const currentUnit = params.get("unit") ?? unitId;
  if (currentDay && /^\d+$/.test(currentDay))
    authorParams.set("previewSession", currentDay);
  if (currentUnit && allowed.has(currentUnit))
    authorParams.set("previewUnit", currentUnit);
  const authorReturnPath = `${from === "admin" ? "/admin/journey-builder" : "/create/journey"}/${snapshot?.sourceId || (snapshot ? "new" : journey.id)}${authorParams.size ? `?${authorParams}` : ""}`;
  const value: CoursePreviewContextValue = {
    isUnsaved: Boolean(snapshot),
    journeyId: journey.id,
    unitId,
    completeWriting,
    completedUnitIds: completed,
    completeUnit,
    reset,
    navigation: {
      preview: true,
      previewFrom: from,
      ...(snapshot ? { previewSnapshot: snapshot.token } : {}),
    },
    authorReturnPath,
  };
  return (
    <CoursePreviewContext.Provider value={value}>
      <WritingPreviewProvider
        value={{
          completeWriting,
          returnPath: getCourseReturnPath(params) || authorReturnPath,
          previewKey: key,
          isUnsaved: Boolean(snapshot),
          completionImageUrl: journeyCompletionArtwork(journey),
        }}
      >
        {typeof children === "function" ? children(journey) : children}
      </WritingPreviewProvider>
    </CoursePreviewContext.Provider>
  );
}

/** Freestyle has no program route parameter; PowerPrompt binds its actual route ID. */
export function CourseWritingPreviewBoundary({
  children,
  surface,
}: {
  children: ReactNode;
  surface: "powerprompt" | "freestyle";
}) {
  const { programId } = useParams();
  return (
    <CoursePreviewBoundary surface={surface} programId={programId}>
      {children}
    </CoursePreviewBoundary>
  );
}
